<template>
  <div class="sales-dashboard min-h-screen">
    <!-- Header with Filters -->
    <SalesDashboardHeader 
      activeTab="overview" 
      :kpis="kpis"
      :showKpis="false"
      :showFilters="true"
      :filters.sync="localFilters"
      :sellers="sellers"
      :refreshing="isRefreshing"
      @apply="fetchDashboardData"
      @refresh="refreshData"
      @export-excel="exportToExcel"
    />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <LoadingSpinner v-if="isLoading" :isLoading="isLoading" />
      <div v-else>
        <!-- KPI Cards Section - Above Insights -->
        <div v-if="kpis" class="sd-card p-6 mb-6" style="overflow: visible;">
          <div class="flex items-center gap-2 mb-6">
            <svg class="w-5 h-5 text-securdia-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
            </svg>
            <h3 class="text-lg font-semibold tracking-tight text-secondary-800">
              {{ $t('sales_dashboard.key_metrics') || 'Nyckeltal' }}
            </h3>
          </div>
          
          <!-- RAD 1: LÖPANDE STOCK (MRR/ARR) -->
          <div class="mb-3">
            <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              <!-- MRR -->
            <div class="sd-kpi-card sd-kpi-card--compact sd-kpi-card--border-blue">
              <div class="flex items-center justify-between mb-1">
                <span class="sd-kpi-card__title">MRR</span>
                <span v-if="kpis.mrrGrowth" class="sd-kpi-card__trend sd-kpi-card__trend--positive">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
                  </svg>
                  {{ kpis.mrrGrowth }}%
                </span>
              </div>
              <p class="sd-kpi-card__value text-blue-600">{{ formatCurrency(kpis.mrr) }}</p>
              <p class="text-xs text-gray-500 mt-0.5">{{ $t('sales_dashboard.active_subscriptions') }}</p>
            </div>

            <!-- ARR -->
            <div class="sd-kpi-card sd-kpi-card--compact sd-kpi-card--border-blue">
              <span class="sd-kpi-card__title">ARR</span>
              <p class="sd-kpi-card__value text-blue-600 mt-1">{{ formatCompactCurrency(kpis.arr) }}</p>
            </div>

            <!-- Aktiva avtal -->
            <div class="sd-kpi-card sd-kpi-card--compact sd-kpi-card--border-blue">
              <span class="sd-kpi-card__title">{{ $t('sales_dashboard.active_contracts_label') }}</span>
              <p class="sd-kpi-card__value text-blue-600 mt-1">{{ kpis.activeContracts }}</p>
            </div>

            <!-- Churn Rate -->
            <div 
              class="sd-kpi-card sd-kpi-card--compact sd-kpi-card--border-blue cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all"
              @click="showChurnedContracts"
            >
              <div class="flex items-center justify-between mb-1">
                <span class="sd-kpi-card__title">Churn Rate</span>
                <div class="flex items-center gap-2">
                  <span v-if="kpis.churnedCount > 0" class="text-xs text-gray-500">
                    {{ kpis.churnedCount }} {{ $t('sales_dashboard.ended_label') }}
                  </span>
                  <svg class="w-4 h-4 text-gray-400 hover:text-secondary-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
              </div>
              <p class="sd-kpi-card__value text-blue-600 mt-1">{{ formatPercent(kpis.churnRate) }}%</p>
              <p class="text-xs text-gray-500 mt-0.5">{{ $t('sales_dashboard.of_subscriptions') }}</p>
            </div>
            </div>
          </div>

          <!-- RAD 2: NYA AFFÄRER (Perioden) -->
          <div class="mb-3">
            <div class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4">

              <!-- Ny MRR -->
              <div class="sd-kpi-card sd-kpi-card--compact sd-kpi-card--border-green">
                <span class="sd-kpi-card__title">{{ $t('sales_dashboard.new_mrr') }}</span>
                <p class="sd-kpi-card__value text-green-600 mt-1">{{ formatCurrency(kpis.newMrr) }}</p>
                <p class="text-xs text-gray-500 mt-0.5">{{ $t('sales_dashboard.period') }}</p>
              </div>

              <!-- Uppstartsavgifter -->
              <div class="sd-kpi-card sd-kpi-card--compact sd-kpi-card--border-green">
                <span class="sd-kpi-card__title">{{ $t('sales_dashboard.setup_fees') }}</span>
                <p class="sd-kpi-card__value text-green-600 mt-1">{{ formatCompactCurrency(kpis.totalSetupFees) }}</p>
              </div>

              <!-- Kontantaffärer -->
              <div class="sd-kpi-card sd-kpi-card--compact sd-kpi-card--border-green">
                <span class="sd-kpi-card__title">{{ $t('sales_dashboard.cash_sales') }}</span>
                <p class="sd-kpi-card__value text-green-600 mt-1">{{ formatCompactCurrency(kpis.totalCashRevenue) }}</p>
              </div>
            </div>
          </div>

          <!-- RAD 3: PRODUKTER (Stock vs Sålda) -->
          <div class="mb-3" style="overflow: visible;">
            <div class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4" style="overflow: visible;">
              <!-- Personlarm Stock -->
            <div class="sd-kpi-card sd-kpi-card--compact sd-kpi-card--border-purple" style="overflow: visible !important;">
              <div class="flex items-center justify-between mb-1">
                <span class="sd-kpi-card__title">{{ $t('sales_dashboard.alarm_stock') }}</span>
                <span class="relative group" style="z-index: 100;">
                  <svg class="w-4 h-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style="z-index: 9999;">
                    {{ $t('sales_dashboard.stock_mrr_tooltip') }}
                  </span>
                </span>
              </div>
              <p class="sd-kpi-card__value text-purple-600">{{ kpis.stockAlarmsCount || 0 }} {{ $t('sales_dashboard.pieces_unit') }}</p>
              <p v-if="localFilters.category === ''" class="text-xs mt-0.5 text-gray-400 italic">{{ $t('sales_dashboard.avg_price') }}: {{ $t('sales_dashboard.not_comparable') }}</p>
              <p v-else class="text-xs mt-0.5 text-gray-500">{{ $t('sales_dashboard.avg_price') }}: {{ formatCurrency(kpis.avgStockAlarmPrice) }}</p>
            </div>
            
            <!-- Personlarm Sålda (nya i perioden) -->
            <div class="sd-kpi-card sd-kpi-card--compact sd-kpi-card--border-purple">
              <div class="flex items-center justify-between mb-1">
                <span class="sd-kpi-card__title">{{ $t('sales_dashboard.alarm_sold') }}</span>
              </div>
              <p class="sd-kpi-card__value text-purple-600">{{ kpis.soldAlarmsCount || 0 }} {{ $t('sales_dashboard.pieces_unit') }}</p>
              <p v-if="localFilters.category === ''" class="text-xs mt-0.5 text-gray-400 italic">{{ $t('sales_dashboard.avg_price') }}: {{ $t('sales_dashboard.not_comparable') }}</p>
              <p v-else class="text-xs mt-0.5 text-gray-500">{{ $t('sales_dashboard.avg_price') }}: {{ formatCurrency(kpis.avgSoldAlarmPrice) }}</p>
            </div>

            <!-- Skalskydd Stock -->
            <div class="sd-kpi-card sd-kpi-card--compact sd-kpi-card--border-purple" style="overflow: visible !important;">
              <div class="flex items-center justify-between mb-1">
                <span class="sd-kpi-card__title">{{ $t('sales_dashboard.perimeter_stock') }}</span>
                <span class="relative group" style="z-index: 100;">
                  <svg class="w-4 h-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <span class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style="z-index: 9999;">
                    {{ $t('sales_dashboard.stock_mrr_tooltip') }}
                  </span>
                </span>
              </div>
              <p class="sd-kpi-card__value text-purple-600 mt-1">{{ kpis.stockAjaxCount || 0 }} {{ $t('sales_dashboard.pieces_unit') }}</p>
              <p v-if="localFilters.category === ''" class="text-xs mt-0.5 text-gray-400 italic">{{ $t('sales_dashboard.avg_price') }}: {{ $t('sales_dashboard.not_comparable') }}</p>
              <p v-else class="text-xs mt-0.5 text-gray-500">{{ $t('sales_dashboard.avg_price') }}: {{ formatCurrency(kpis.avgStockAjaxPrice) }}</p>
            </div>
            
              <!-- Skalskydd Sålda -->
              <div class="sd-kpi-card sd-kpi-card--compact sd-kpi-card--border-purple">
                <span class="sd-kpi-card__title">{{ $t('sales_dashboard.perimeter_sold') }}</span>
                <p class="sd-kpi-card__value text-purple-600 mt-1">{{ kpis.soldAjaxCount || 0 }} {{ $t('sales_dashboard.pieces_unit') }}</p>
                <p v-if="localFilters.category === ''" class="text-xs mt-0.5 text-gray-400 italic">{{ $t('sales_dashboard.avg_price') }}: {{ $t('sales_dashboard.not_comparable') }}</p>
                <p v-else class="text-xs mt-0.5 text-gray-500">{{ $t('sales_dashboard.avg_price') }}: {{ formatCurrency(kpis.avgSoldAjaxPrice) }}</p>
              </div>
            </div>
          </div>

          <!-- RAD 4: KVALITET & EFFEKTIVITET -->
          <div>
            <div class="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <div class="flex items-center">
                <KPICard 
                  :title="$t('sales_dashboard.avg_margin')"
                  :value="kpis.avgMarginPercent"
                  :subtitle="$t('sales_dashboard.gross_margin')"
                  icon="percentage"
                  format="percentage"
                  color="accent"
                  variant="compact"
                  :horizontal="true"
                  class="w-full sd-kpi-card--border-orange"
                />
              </div>
              <div class="flex items-center">
                <KPICard 
                  :title="$t('sales_dashboard.avg_contract_value')"
                  :value="kpis.avgContractValue"
                  :subtitle="$t('sales_dashboard.total_per_contract')"
                  icon="coins"
                  format="currency"
                  color="accent"
                  variant="compact"
                  :horizontal="true"
                  class="w-full sd-kpi-card--border-orange"
                />
              </div>
              <div class="flex items-center">
                <KPICard 
                  :title="$t('sales_dashboard.avg_cash_sale')"
                  :value="kpis.avgCashSale"
                  :subtitle="`${kpis.cashSalesCount || 0} ${$t('sales_dashboard.sales')}`"
                  icon="money-bill"
                  format="currency"
                  color="accent"
                  variant="compact"
                  :horizontal="true"
                  class="w-full sd-kpi-card--border-orange"
                />
              </div>
              <div class="flex items-center">
                <KPICard 
                  :title="$t('sales_dashboard.avg_contract_length')"
                  :value="Math.round(kpis.avgContractLengthMonths || 0)"
                  subtitle="månader"
                  icon="clock"
                  format="number"
                  color="accent"
                  variant="compact"
                  :horizontal="true"
                  class="w-full sd-kpi-card--border-orange"
                />
              </div>
              <div class="flex items-center">
                <KPICard 
                  :title="$t('sales_dashboard.total_products_sold')"
                  :value="kpis.totalProductsSold"
                  :subtitle="$t('sales_dashboard.all_contracts')"
                  icon="box"
                  format="number"
                  color="accent"
                  variant="compact"
                  :horizontal="true"
                  class="w-full sd-kpi-card--border-orange"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Insights Card -->
        <InsightsCard 
          :contracts="contracts"
          :kpis="kpis"
          :salespersons="salespersons"
          :productStats="productStats"
          class="mb-6"
        />

        <!-- Product Analytics Component -->
        <ProductAnalytics 
          :productStats="productStats"
          class="mb-6"
        />

        <!-- Sales Trend Chart (All Years) -->
        <SalesTrendChart 
          :contracts="contracts"
          class="mb-6"
        />

        <!-- Advanced Analysis Row -->
        <div class="grid grid-cols-1 gap-6 mb-6">
          <!-- Seller Analysis Advanced -->
          <SellerAnalysisAdvanced 
            :salespersons="salespersons"
            :contracts="contracts"
          />
        </div>

        <!-- Charts Row -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-stretch">
          <!-- Salesperson Analytics -->
          <div class="lg:col-span-1 flex flex-col">
            <SalespersonAnalytics 
              :salespersons="salespersons"
              @select-salesperson="selectSalesperson"
              class="flex-1"
            />
          </div>

          <!-- Product Distribution Chart med kategorier/tillverkare -->
          <div class="lg:col-span-1 flex flex-col">
            <ProductDistributionChart 
              :productStats="productStats.products || []"
              :contracts="contracts"
              class="flex-1"
            />
          </div>
        </div>

        <!-- Analysis Row -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <!-- Top Contracts -->
          <TopContracts 
            :contracts="contracts"
            :salespersons="salespersons"
            :loading="contractsLoading"
          />
          
          <!-- Low Margin Warnings -->
          <LowMarginWarnings 
            :contracts="contracts"
            :salespersons="salespersons"
            @select-salesperson="selectSalesperson"
          />
        </div>

        <!-- Contracts List -->
        <ContractsList 
          :contracts="contracts"
          :loading="contractsLoading"
        />
      </div>
    </div>

    <!-- Churned Contracts Modal -->
    <ChurnedContractsModal :contracts="contracts" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import KPICard from '@/components/sales/KPICard.vue';
import SalespersonAnalytics from '@/components/sales/SalespersonAnalytics.vue';
import ContractsList from '@/components/sales/ContractsList.vue';
import InsightsCard from '@/components/sales/InsightsCard.vue';
import TopContracts from '@/components/sales/TopContracts.vue';
import LowMarginWarnings from '@/components/sales/LowMarginWarnings.vue';
import ProductAnalytics from '@/components/sales/ProductAnalytics.vue';
import SalesDashboardHeader from '@/components/sales/SalesDashboardHeader.vue';
import SalesTrendChart from '@/components/sales/SalesTrendChart.vue';
import SellerAnalysisAdvanced from '@/components/sales/SellerAnalysisAdvanced.vue';
import ProductDistributionChart from '@/components/sales/ProductDistributionChart.vue';
import ChurnedContractsModal from '@/components/sales/ChurnedContractsModal.vue';

export default {
  name: 'SalesDashboard',
  title() {
    return `${this.$t('sales_dashboard.title')} | SecurCloud`;
  },
  components: {
    KPICard,
    SalespersonAnalytics,
    ContractsList,
    InsightsCard,
    TopContracts,
    LowMarginWarnings,
    ProductAnalytics,
    SalesDashboardHeader,
    SalesTrendChart,
    SellerAnalysisAdvanced,
    ProductDistributionChart,
    ChurnedContractsModal
  },
  data() {
    return {
      // Local filter state for v-model binding
      localFilters: {
        sellerId: '',
        year: new Date().getFullYear().toString(),
        quarter: '',
        month: '',
        category: 'rental', // Default to rental agreements
        contractType: 'all',
        startDate: '',
        endDate: '',
        dateFilterType: 'contract_start' // 'contract_start' for ARR/MRR, 'pdf_upload_date' for ACV
      },
      isRefreshing: false,
      hasInitialized: false
    };
  },
  computed: {
    ...mapGetters('salesDashboard', [
      'kpis',
      'monthlyRevenue', 
      'productStats',
      'salespersons',
      'contracts',
      'velocity',
      'products',
      'sellers',
      'filters',
      'selectedYear',
      'isLoading',
      'contractsLoading',
      'error'
    ]),
    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= currentYear - 5; y--) {
        years.push(y);
      }
      return years;
    }
  },
  watch: {
    // Sync local filters with store filters
    filters: {
      handler(newFilters) {
        // On initial load, default to current year and rental category
        if (!this.hasInitialized) {
          this.localFilters = { 
            ...newFilters,
            year: newFilters.year || new Date().getFullYear().toString(),
            category: newFilters.category || 'rental' // Default to rental
          };
        } else {
          // Only update if actually different to prevent loops
          const newStr = JSON.stringify(newFilters);
          const localStr = JSON.stringify(this.localFilters);
          if (newStr !== localStr) {
            this.localFilters = { ...newFilters };
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    ...mapActions('salesDashboard', [
      'fetchAllDashboardData',
      'refreshAllData',
      'updateFilters',
      'resetFilters',
      'setSelectedYear',
      'fetchMonthlyRevenue',
      'clearCache'
    ]),
    
    async refreshData() {
      this.isRefreshing = true;
      try {
        // Clear cache first
        this.clearCache();
        // Then fetch fresh data
        await this.refreshAllData();
      } finally {
        this.isRefreshing = false;
      }
    },
    
    async fetchDashboardData(showLoading = false) {
      // Update store filters from local filters (only if different to prevent loops)
      const currentFiltersStr = JSON.stringify(this.filters);
      const localFiltersStr = JSON.stringify(this.localFilters);
      if (currentFiltersStr !== localFiltersStr) {
        this.updateFilters(this.localFilters);
      }
      
      // Fetch data without showing loading spinner (to avoid UI flicker on filter change)
      await this.fetchAllDashboardData({ showLoading });
    },
    
    async initialLoad() {
      // Show loading spinner on initial page load
      await this.fetchAllDashboardData({ showLoading: true, force: false });
    },
    
    async handleYearChange() {
      this.setSelectedYear(this.selectedYear);
      await this.fetchMonthlyRevenue();
    },
    
    selectSalesperson(salesperson) {
      this.localFilters.sellerId = salesperson.id;
      this.fetchDashboardData();
    },
    
    showChurnedContracts() {
      this.$modal.show('churned-contracts-modal');
    },
    
    formatCurrency(value) {
      if (!value && value !== 0) return '-';
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },
    
    formatCompactCurrency(value) {
      if (!value && value !== 0) return '-';
      if (Math.abs(value) >= 1000000) {
        return `${(value / 1000000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })}M kr`;
      }
      if (Math.abs(value) >= 1000) {
        return `${(value / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 0 })}k kr`;
      }
      return this.formatCurrency(value);
    },
    
    formatPercent(value) {
      if (!value && value !== 0) return '0';
      return value.toFixed(1);
    },
    
    formatDecimal(value) {
      if (!value && value !== 0) return '-';
      return value.toFixed(1);
    },
    
    formatYears(months) {
      if (!months) return '';
      const years = (months / 12).toFixed(1);
      return `~${years} ${this.$t('sales_dashboard.years')}`;
    },
    
    exportToExcel() {
      // Build export data from contracts and KPIs
      const data = this.contracts.map(contract => ({
        'Avtalsnummer': contract.agreement_number || '-',
        'Kund': contract.customer_name || '-',
        'Säljare': contract.salesperson_name || '-',
        'Kategori': contract.category || '-',
        'Status': contract.status || '-',
        'Startdatum': contract.contract_start ? new Date(contract.contract_start).toLocaleDateString('sv-SE') : '-',
        'Slutdatum': contract.contract_end ? new Date(contract.contract_end).toLocaleDateString('sv-SE') : '-',
        'Avtalslängd (mån)': contract.contract_length_months || 0,
        'MRR (kr)': contract.mrr || 0,
        'Totalt värde (kr)': contract.total_value || 0,
        'Antal produkter': contract.products?.length || 0
      }));

      // Create CSV content
      if (data.length === 0) {
        this.$toast?.warning(this.$t('alarm_pdf.no_data_to_export'));
        return;
      }

      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(';'),
        ...data.map(row => headers.map(h => {
          const val = row[h];
          // Escape values with semicolons or quotes
          if (typeof val === 'string' && (val.includes(';') || val.includes('"'))) {
            return `"${val.replace(/"/g, '""')}"`;
          }
          return val;
        }).join(';'))
      ].join('\n');

      // Add BOM for Excel UTF-8 compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create download link
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `sales_dashboard_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      this.$toast?.success('Export klar!');
    }
  },
  created() {
    this.setPageTitle(this.$t('sales_dashboard.title'), 'sales_dashboard');
    // Ensure year filter is set to current year before fetching data
    if (!this.localFilters.year) {
      this.localFilters.year = new Date().getFullYear().toString();
    }
    this.updateFilters(this.localFilters);
    this.initialLoad();
    // Mark as initialized after first load - allows user to select "Alla år" afterwards
    this.hasInitialized = true;
  }
};
</script>

<style scoped>
/* Additional scoped styles if needed */
</style>
